// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getQuotesCollection from "@bg/App/Handlers/CustomQuote/GetCustomQuotesCollection";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFromSearchEngines,
    HideFromSiteSearch,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    customQuotesDataView: state.myCustomQuotes,
    getCustomQuotesParameter: state.myCustomQuotes.getCustomQuotesParameter,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    location: getLocation(state),
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    getQuotesCollection,
};

type Props = ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    PageProps &
    HasHistory &
    HasShellContext;

const MyCustomQuotes = ({
    id,
    customQuotesDataView,
    getCustomQuotesParameter,
    getQuotesCollection,
    isAuthenticated,
    location,
    signInUrl,
    history,
    shellContext,
}: Props) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isAuthenticated && !shellContext.isInShell) {
            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(location.pathname + location.search)}`);
        }
    }, []);

    useEffect(() => {
        if (getCustomQuotesParameter && !customQuotesDataView.isLoading && isAuthenticated) {
            setIsLoading(true);
            getQuotesCollection({
                page: getCustomQuotesParameter.page,
                pageSize: getCustomQuotesParameter.pageSize,
                sort: getCustomQuotesParameter.sort,
                search: getCustomQuotesParameter.search,
                onError: result => {},
                onComplete: (result: any) => {
                    if (result) {
                        setIsLoading(false);
                    }
                },
            });
        }
    }, [getCustomQuotesParameter]);

    if (!isAuthenticated && !shellContext.isInShell) {
        return null;
    }

    return (
        <Page>
            <LoadingOverlay loading={isLoading}>
                <Zone contentId={id} zoneName="Content" />
                <Modals />
            </LoadingOverlay>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withIsInShell(connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuotes))),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [ExcludeFromNavigation, ExcludeFromSignInRequired, HideFromSearchEngines, HideFromSiteSearch],
    },
};

export default pageModule;

export const MyCustomQuotesContext = "MyCustomQuotesPage";
